import {Form} from 'antd';
import cn from 'classnames';
import {isArray, get} from 'lodash-es';
import moment from 'moment';
import React from 'react';

import {AIS_ROUTES} from 'components/ais/ais.constants';
import {FactorInfluenceInfoItemModalOpener} from 'components/ais/factor-influence-info';
import {
    FACTOR_INFLUENCE_INFO_ITEM_MODAL_BUTTON_TYPE,
    FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS,
} from 'components/ais/factor-influence-info/modals/factor-influence-info-item-modal/model/constants';
import {ProjectInvestmentInfoItemModalOpener} from 'components/ais/project-investment-info';
import {
    PROJECT_INVESTMENT_INFO_ITEM_MODAL_BUTTON_TYPE,
    PROJECT_INVESTMENT_INFO_ITEM_MODAL_TAB_KEYS,
} from 'components/ais/project-investment-info/modals/project-investment-info-item-modal/model/project-investment-info-item-modal.constants';
import {QueryParamLink} from 'components/query-param-link';
import {getColumnRedirectParams} from 'components/table-report/table-report-columns/table-report-columns-utils';
import {MonitorServicesContentDisplayer} from 'components/table/columns/monitor-services-content-displayer/monitor-services-content-displayer';
import {EMonitorServiceContentDisplayerType} from 'components/table/columns/monitor-services-content-displayer/monitor-services-content-displayer.types';
import {IFieldsState} from 'components/table/table-types';
import {IEntityMeta, IFieldMeta, EFieldType, EMetaActionType} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {ETablePageQueryParams} from 'pages/table-page/query-params-aware-table/constants/table-query-params.constants';
import {EEntityType} from 'shared/constants/entities';
import {EFileType} from 'shared/types/files';
import {TStateSetter} from 'shared/types/generics';
import {combineString} from 'shared/utils/combine-string';

import {Counter} from '../counter';
import {BoolField} from '../form/inputs/bool-field';
import {CheckboxField} from '../form/inputs/checkbox-field';
import {EditableField} from '../form/inputs/editable-field/editable-field';
import {ReportLinkField} from '../form/inputs/report-link-field/report-link-field';
import {RequestLink} from '../form/inputs/request-link/request-link';
import {RichTextRenderer} from '../rich-text';
import {ActionDeleteCell} from './columns/action-delete-cell/action-delete-cell';
import {AdditionalInformationCell} from './columns/additional-information-cell/additional-information-cell';
import {BoolCellString} from './columns/bool-cell-string/bool-cell-string';
import {BoolCell} from './columns/bool-cell/bool-cell';
import {BreadcrumbItem} from './columns/breadcrumb-item/breadcrumb-item';
import {DownloadFileLink} from './columns/download-file-link-cell';
import {FactorInfluenceStatus} from './columns/factor-influence-status-cell/factor-influence-status-cell';
import {FileColumn} from './columns/file';
import {FileUploadCell} from './columns/file-upload-cell';
import {IconsDownloadCell} from './columns/icon-download-cell';
import {IconsCell} from './columns/icons-cell';
import {FIELD_TYPE_TO_SQUARE_VARIANT_MAP, NumberSquare} from './columns/number-square/number-square';
import {OpenDocumentCodesModalAction} from './columns/open-document-codes-modal-actions/open-document-codes-modal-actions';
import {OpenModalAction} from './columns/open-modal-action/open-modal-action';
import {OpenReportConfigurationHistoryModal} from './columns/open-report-configuration-history-modal/open-report-configuration-history-modal';
import {OpenSettingsPageButton} from './columns/open-settings-page-button/open-settings-page-button';
import {ReportWatcher} from './columns/report-watcher/report-watcher';
import {SignatureReference} from './columns/signature-reference';
import {Status} from './columns/status-cell/status-cell';
import {UploadScanFilesCell} from './columns/upload-scan-files-cell';
import {
    createStringCell, 
    createStringDate, 
    createTableCellClassNameResolver, 
    isDatePassed
} from './utils/table.utils';
import './table.less';

export const createTableColumnResolver = (
    key: string,
    field: IFieldMeta,
    url: string,
    options: Pick<IEntityMeta, 'linkField'>,
    entityName: string,
    entityType?: EEntityType,
    checkKey?: any,
    openModalForm?: (modalName: string, record: any, parentEntityName?: string) => void,
    handleBreadcrumbsEntityName?: (newEntityName: string, metaTable: IEntityMeta) => void,
    handleChangeIndexRow?: (value: TEntity, key: string, dependenceFieldKey?: string) => void,
    prefix?: string,
    params?: Record<string, any>,
    setFieldsState?: TStateSetter<IFieldsState>,
) => (
    value: TEntityValue,
    record: TEntity,
    rowIndex: number,
) => {
    let cell;
    const isRecordNotRead = get(record, 'readFlag') === false;
    const isFileExchangeCellBold = (
        field.key === 'name' || field.key === 'downloadDate' || field.key === 'mainDocNumber'
    ) && (
        entityName === AIS_ROUTES.FILE_EXCHANGE_ENTITY_NAME
        || entityName === AIS_ROUTES.EXPLANATION_INFORMATION
        || entityName === AIS_ROUTES.RECLAIMING_INFORMATION
    ) && record.openCard === false;
    const cellClassName = cn(
        'table-container__text',
        field.ellipsis && 'ellipsis',
        isRecordNotRead && 'accented',
        isFileExchangeCellBold && 'cell-text_bold',
        createTableCellClassNameResolver(entityName, field.key),
    );

        switch (field.type) {
            case EFieldType.ICON:
                cell = (
                    <IconsCell
                        record={record}
                        field={field}
                    />
                );
                break;
            case EFieldType.ICON_DOWNLOAD:
                cell = (
                    <IconsDownloadCell
                        record={record}
                        field={field}
                    />
                );
                break;
            case EFieldType.COUNTER:
                cell = (
                    <Counter
                        count={value as number}
                        color={field?.color}
                    />
                );
                break;
            case EFieldType.BREADCRUMB_ITEM:
                cell = (
                    <BreadcrumbItem
                        record={record}
                        field={field}
                        entityName={entityName}
                        entityType={entityType}
                        handleBreadcrumbsEntityName={handleBreadcrumbsEntityName}
                    />
                );
                break;
            case EFieldType.UPLOAD_SCAN_FILES:
                cell = (
                    <UploadScanFilesCell
                        fieldType={key}
                        value={value}
                        record={record}
                        entityType={entityType ?? EEntityType.TABLE}
                        entityName={entityName}
                    />
                );
                break;
            case EFieldType.FILE:
            case EFieldType.ATTORNEY:
            case EFieldType.SIGNATURE:
                cell = (
                    <FileColumn
                        fieldType={key}
                        type={field.type}
                        value={value}
                        record={record}
                        entityType={entityType}
                        entityName={entityName}
                        actions={field.actions}
                    />
                );
                break;
            case EFieldType.SIGNATURE_REFERENCE:
                cell = (
                    <SignatureReference
                        value={value}
                        record={record}
                        entityName={entityName}
                        fieldMeta={field}
                    />
                );
                break;
            case EFieldType.SIGNATURE_DOWNLOAD_LINK:
            case EFieldType.FILE_DOWNLOAD_LINK: {
                const topIcon = field?.fieldStructure?.[0]?.iconName;
                cell = (
                    <DownloadFileLink
                        value={value}
                        record={record}
                        stringStructure={field.stringStructure}
                        icon={topIcon}
                        fileType={
                            field.type === EFieldType.SIGNATURE_DOWNLOAD_LINK ? EFileType.SIGNATURE : EFileType.FILE
                        }
                        entityName={entityName}
                    />
                );
                break;
            }
            case EFieldType.FILE_UPLOAD_CELL:
            case EFieldType.SIGNATURE_UPLOAD_CELL:
                cell = (
                    <FileUploadCell
                        value={value}
                        fieldMeta={field}
                        record={record}
                        entityName={entityName}
                        isSignUpload={field.type === EFieldType.SIGNATURE_UPLOAD_CELL}
                    />
                );
                break;
            case EFieldType.BOOLEAN_STRING:
                cell = (
                    <BoolCellString
                        field={field}
                        value={get(record, field.key)}
                    />
                );
                break;
            case EFieldType.COMBINED_STRING: {
                const {stringStructure, type} = field;
                const dataForCombinedIntoString = typeof value === 'object' ? value : record;
                if (!stringStructure) {
                    throw new Error(`Отсутствует stringStructure для поля с типом ${type}`);
                }

                let combinedString;
                if (Array.isArray(dataForCombinedIntoString)) {
                    combinedString = dataForCombinedIntoString
                        .map((item) => combineString(stringStructure, item))
                        .join(', ');
                } else {
                    combinedString = combineString(stringStructure, dataForCombinedIntoString, field?.dateFormat);
                }
                cell = <span className="field-doc">{combinedString}</span>;
                break;
            }

            case EFieldType.BOOLEAN:
                cell = (
                    <BoolCell
                        field={field}
                        value={value}
                        record={record}
                        handleChangeIndexRow={handleChangeIndexRow}
                        setFieldsState={setFieldsState}
            />
                );
                break;
            case EFieldType.ADDITIONAL_INFORMATION:
                cell = (
                    <AdditionalInformationCell
                        record={record}
                        field={field}
                        parentEntityName={field?.parentEntityName || ''}
                        entityName={entityName}
                    />
                );
                break;
            case EFieldType.ACTION_DELETE:
                cell = (
                    <ActionDeleteCell
                        entityName={entityName}
                        record={record}
                    />
                );
                break;
            case EFieldType.DATE:
                cell = createStringDate(value, field?.dateFormat);
                break;
            case EFieldType.DATE_PASSED_INDICATOR: {
                cell = (
                    <div
                        className={cn(
                            'date-passed-indicator',
                            isDatePassed(value, record) && 'date-passed-indicator_expired',
                        )}
                    >
                        {createStringDate(value, field?.dateFormat)}
                    </div>
                );
                break;
            }
            case EFieldType.COMBINED_STRING_DATE: {
                const {stringStructure, dateFormat} = field;
                if (stringStructure) {
                    const date = moment(combineString(stringStructure, record));
                    if (date.isValid()) {
                        cell = date.format(dateFormat);
                    }
                }
                break;
            }
            case EFieldType.CHECKBOX: {
                const linkField = options.linkField || 'id';
                const formItemName = `${field.key}_${record[linkField]}`;

                cell = (
                    <Form.Item
                        name={formItemName}
                        key={formItemName}
                        initialValue={value}
                    >
                        <BoolField
                            key={field.key}
                            value={value}
                        />
                    </Form.Item>
                );
                break;
            }

            case EFieldType.STRING:
            case EFieldType.NUMBER:
                if (field.index) {
                    const cellArr = get(record, field.key);
                    const index = field.index - 1;
                    if (cellArr) {
                        cell = cellArr[index as keyof typeof cellArr];
                    }
                    break;
                }
                cell = get(record, field.key);
                if (isArray(cell)) {
                    cell = createStringCell(cell, field?.stringStructure);
                }
                break;

            case EFieldType.CHECKBOX_GROUP:
                cell = (
                    <CheckboxField
                        field={field}
                        record={record}
                        value={value}
                        disabled={field.isDisabled}
                        checkKey={checkKey}
                        entityName={entityName}
                    />
                );
                break;

            case EFieldType.REQUEST_LINK: {
                cell = (
                    <RequestLink
                        field={field}
                        record={record}
                        url={url}
                        entityName={entityName || ''}
                    />
                );
                break;
            }

            case EFieldType.REPORT_LINK:
                cell = (
                    <ReportLinkField
                        entityName={entityName}
                        value={record}
                        fieldMeta={field}
                        entityType={entityType}
                        prefix={prefix}
                        url={url}
                        record={record}
                        params={params}
                    />
                );
                break;

            case EFieldType.EDITABLE_FIELD:
                cell = (
                    <EditableField
                        entityName={entityName}
                        value={record}
                        fieldMeta={field}
                        entityType={entityType}
                        prefix={prefix}
                        url={url}
                        params={params}
                    />
                );
                break;
            case EFieldType.OPEN_MODAL_ACTION: {
                cell = (
                    <OpenModalAction
                        url={url}
                        entityName={entityName}
                        record={record}
                        field={field}
                    />
                );
                break;
            }
            case EFieldType.FACTOR_INFLUENCE_INFO_DETAIL_BUTTON: {
                cell = record?.existDetailedInformationOnMacroeconomicFactors ? (
                    <FactorInfluenceInfoItemModalOpener
                        buttonViewType={FACTOR_INFLUENCE_INFO_ITEM_MODAL_BUTTON_TYPE.DETAIL}
                        record={record}
                        entityName={entityName}
                        meta={{actionType: EMetaActionType.OPEN_MODAL, actionTitle: '...', actionCode: 'CREATE'}}
                        tabKey={FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS.ADDITIONAL}
                    />
                ) : null;
                break;
            }
            case EFieldType.FACTOR_INFLUENCE_INFO_DATE_BUTTON: {
                cell = (
                    <FactorInfluenceInfoItemModalOpener
                        buttonViewType={FACTOR_INFLUENCE_INFO_ITEM_MODAL_BUTTON_TYPE.LINK_DATE}
                        record={record}
                        entityName={entityName}
                        meta={{actionType: EMetaActionType.OPEN_MODAL, actionTitle: 'Дата', actionCode: 'CREATE'}}
                    />
                );
                break;
            }
            case EFieldType.FACTOR_INFLUENCE_STATUS: {
                cell = <FactorInfluenceStatus value={value} />;
                break;
            }
            case EFieldType.PROJECT_INVESTMENT_INFO_DETAIL_BUTTON: {
                cell = record?.detailedInformation ? (
                    <ProjectInvestmentInfoItemModalOpener
                        buttonViewType={PROJECT_INVESTMENT_INFO_ITEM_MODAL_BUTTON_TYPE.DETAIL}
                        record={record}
                        entityName={entityName}
                        meta={{
                            actionType: EMetaActionType.OPEN_MODAL,
                            actionTitle: '...',
                            actionCode: 'CREATE',
                        }}
                        tabKey={PROJECT_INVESTMENT_INFO_ITEM_MODAL_TAB_KEYS.MAIN}
                    />
                ) : null;
                break;
            }
            case EFieldType.PROJECT_INVESTMENT_INFO_DATA_BUTTON: {
                cell = (
                    <ProjectInvestmentInfoItemModalOpener
                        buttonViewType={PROJECT_INVESTMENT_INFO_ITEM_MODAL_BUTTON_TYPE.LINK_DATA}
                        record={record}
                        entityName={entityName}
                        meta={{
                            actionType: EMetaActionType.OPEN_MODAL,
                            actionTitle: 'Запись',
                            actionCode: 'CREATE',
                        }}
                    />
                );
                break;
            }
            case EFieldType.OPEN_DOCUMENT_CODES_MODAL: {
                cell = (
                    <OpenDocumentCodesModalAction
                        url={url}
                        entityName={entityName}
                        field={field}
                        record={record}
                    />
                );
                break;
            }
            case EFieldType.OPEN_EDIT_MODAL: {
                if (field.isNeedToRedirect) {
                    cell = (
                        <QueryParamLink
                            params={[
                                {name: ETablePageQueryParams.entity, value: record[field.isNeedToRedirect] as string},
                                {name: ETablePageQueryParams.entityType, value: EEntityType.EDIT_FORM},
                            ]}
                        >
                            {get(record, field.key)}
                        </QueryParamLink>
                    );
                }
                break;
            }
            case EFieldType.NUMBER_SQUARE_DEFAULT:
            case EFieldType.NUMBER_SQUARE_PRIMARY:
                cell = (
                    <NumberSquare
                        variant={FIELD_TYPE_TO_SQUARE_VARIANT_MAP[field.type]}
                        value={value}
                    />
                );
                break;
            case EFieldType.RICH_TEXT_RENDERER:
                cell = <RichTextRenderer value={typeof value === 'string' ? value : undefined} />;
                break;
            case EFieldType.TABLE_ROW_INDEX:
                cell = 1 + rowIndex + ((params?.paginationCurrent ?? 1) - 1) * (params?.paginationPageSize ?? 0);
                break;

            case EFieldType.STATUS:
                cell = <Status value={value} />;
                break;

            case EFieldType.REPORT_WATCHER:
                cell = (
                    <ReportWatcher
                        meta={field}
                        record={record}
                        value={value}
                    />
                );
                break;

            case EFieldType.OPEN_REPORT_CONFIGURATION_FORM:
                cell = (
                    <OpenSettingsPageButton
                        queryParams={{name: ETablePageQueryParams.entity, value: record?.templateCode as string}}
                    />
                );
                break;

            case EFieldType.OPEN_FLEX_FIELDS_SETTINGS_PAGE:
                cell = (
                    <OpenSettingsPageButton
                        queryParams={{name: ETablePageQueryParams.dffName, value: record?.name as string}}
                        popoverText="Перейти к настройке ОГП"
                    />
                );
                break;

            case EFieldType.OPEN_REPORT_CONFIGURATION_HISTORY_MODAL:
                cell = <OpenReportConfigurationHistoryModal templateName={record?.templateName as string} />;
                break;

            case EFieldType.AIS3_MS_REQUEST_CONTENT:
            case EFieldType.AIS3_MS_RESPONSE_CONTENT:
                cell = (
                    <MonitorServicesContentDisplayer
                        record={record}
                        type={
                            field.type === EFieldType.AIS3_MS_REQUEST_CONTENT
                                ? EMonitorServiceContentDisplayerType.request
                                : EMonitorServiceContentDisplayerType.response
                        }
                    />
                );
                break;

            default:
                cell = value;
        }

        const shouldUseDefaultRedirect =
            field.type !== EFieldType.OPEN_MODAL_ACTION
            && field.type !== EFieldType.OPEN_EDIT_MODAL
            && field.type !== EFieldType.REQUEST_LINK;

        const redirectParams = getColumnRedirectParams(field, record);
        const cellWithRedirect = redirectParams ? (
            <QueryParamLink params={redirectParams}>{cell}</QueryParamLink>
        ) : (
            cell
        );

        return (
            <div
                style={{color: field?.customColor || ''}}
                className={cellClassName}
            >
                {(field.isNeedToRedirect || field?.redirectOnCondition) && shouldUseDefaultRedirect
                    ? cellWithRedirect
                    : cell}
            </div>
        );
    };
